/**
 * Iter 80 — Cost Control with Earned Value Management (EVM).
 *
 * - Project selector (auto-loads first project)
 * - KPI tiles: BAC, PV, EV, AC, CV, SV, CPI, SPI, EAC, ETC, VAC
 * - S-curve chart (PV / EV / AC)
 * - AC breakdown (Materials vs Subcontractor)
 * - Health badge (on_track / at_risk / critical)
 * - Portfolio table at the bottom
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Activity, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Wallet, Hammer, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PageHeader from '../components/PageHeader';
import { apiGet } from '../lib/api';
import { inrCompact } from '../lib/format';
import { toast } from 'sonner';

const STATUS_TONE = {
  on_track: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, label: 'ON TRACK' },
  at_risk: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'AT RISK' },
  critical: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: AlertTriangle, label: 'CRITICAL' },
};

function fmtIdx(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(2);
}

function VarianceCell({ value }) {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
      <Icon size={12} /> {inrCompact(Math.abs(value))}
    </span>
  );
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.on_track;
  const Icon = tone.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-bold uppercase tracking-widest rounded-sm border ${tone.bg} ${tone.text} ${tone.border}`} data-testid={`evm-status-${status}`}>
      <Icon size={11} /> {tone.label}
    </span>
  );
}

function KPI({ label, value, sub, icon: Icon, accent = 'indigo', testid }) {
  const tones = {
    indigo: 'border-l-[#3b4872] text-[#3b4872]',
    emerald: 'border-l-emerald-600 text-emerald-700',
    amber: 'border-l-amber-500 text-amber-700',
    rose: 'border-l-rose-600 text-rose-700',
    slate: 'border-l-slate-500 text-slate-700',
  };
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tones[accent]} rounded-sm p-3`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon size={13} className="opacity-50" />}
      </div>
      <div className="text-lg font-display font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CostControl() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');  // '' = project rollup; otherwise specific WO id
  const [evm, setEvm] = useState(null);
  const [sCurve, setSCurve] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects'));
    apiGet('/cost-control/dashboard').then(setPortfolio).catch(() => {});
  }, []);

  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  // Reset WO selection when switching projects
  useEffect(() => { setWorkOrderId(''); }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    const woQ = workOrderId ? `?work_order_id=${workOrderId}` : '';
    Promise.all([
      apiGet(`/cost-control/${projectId}/evm${woQ}`),
      apiGet(`/cost-control/${projectId}/s-curve${woQ}`),
    ])
      .then(([e, s]) => { setEvm(e); setSCurve(s); })
      .catch(() => toast.error('Failed to load EVM data'))
      .finally(() => setLoading(false));
  }, [projectId, workOrderId]);

  // Resolve work orders of the currently selected project
  const selectedProject = projects.find(p => p.id === projectId);
  const workOrders = selectedProject?.work_orders || [];

  const m = evm?.metrics;
  const [showBaseline, setShowBaseline] = useState(true);
  const chartData = useMemo(() => (sCurve?.buckets || []).map(b => ({
    name: b.label, PV: b.PV, EV: b.EV, AC: b.AC, baseline_PV: b.baseline_PV,
  })), [sCurve]);

  return (
    <div>
      <PageHeader
        overline="Cost Control"
        title="Earned Value Management"
        subtitle="CPI · SPI · EAC · ETC · VAC — live performance vs baseline schedule"
      />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
            <select
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              data-testid="cc-project-select"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[300px]"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">
              Scope <span className="text-[9px] text-slate-400 normal-case">· customisable</span>
            </label>
            <select
              value={workOrderId}
              onChange={e => setWorkOrderId(e.target.value)}
              data-testid="cc-wo-select"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]"
              disabled={workOrders.length === 0}
            >
              <option value="">📊 Project Rollup (all WOs)</option>
              {workOrders.map(w => (
                <option key={w.id} value={w.id}>{w.code}{w.title ? ` · ${w.title.slice(0, 30)}` : ''}</option>
              ))}
            </select>
          </div>
          {evm?.schedule && (
            <div className="text-[11px] text-slate-600 flex items-center gap-2 pb-2">
              <Calendar size={12} className="text-slate-400" />
              <span>{evm.schedule.start_date} → {evm.schedule.end_date}</span>
              <span className="text-slate-400">·</span>
              <span><b>{evm.schedule.elapsed_days}</b> / {evm.schedule.total_days} days</span>
              <span className="text-slate-400">·</span>
              <span>planned <b>{evm.schedule.planned_pct}%</b></span>
            </div>
          )}
        </div>
        {m && <StatusBadge status={m.status} />}
      </div>

      {loading && <div className="text-center text-xs text-slate-400 uppercase tracking-widest py-12 font-display">Loading EVM…</div>}

      {!loading && m && (
        <>
          {/* Top KPIs — value */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="evm-kpis">
            <KPI label="BAC · Budget at Completion" value={inrCompact(m.BAC)} icon={Wallet} accent="indigo" testid="evm-kpi-bac" />
            <KPI label="PV · Planned Value (today)" value={inrCompact(m.PV)} sub={evm.schedule.planned_pct + '% of BAC'} icon={Calendar} accent="slate" testid="evm-kpi-pv" />
            <KPI label="EV · Earned Value" value={inrCompact(m.EV)} sub={m.pct_complete != null ? `${(m.pct_complete * 100).toFixed(1)}% complete` : '—'} icon={CheckCircle2} accent="emerald" testid="evm-kpi-ev" />
            <KPI label="AC · Actual Cost" value={inrCompact(m.AC)} sub={m.pct_spent != null ? `${(m.pct_spent * 100).toFixed(1)}% of BAC` : '—'} icon={Hammer} accent="amber" testid="evm-kpi-ac" />
          </div>

          {/* Variance + Index */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <KPI label="CV · Cost Variance" value={<VarianceCell value={m.CV} />} sub="EV − AC" accent={m.CV >= 0 ? 'emerald' : 'rose'} testid="evm-kpi-cv" />
            <KPI label="SV · Schedule Variance" value={<VarianceCell value={m.SV} />} sub="EV − PV" accent={m.SV >= 0 ? 'emerald' : 'rose'} testid="evm-kpi-sv" />
            <KPI label="CPI · Cost Performance" value={fmtIdx(m.CPI)} sub={m.CPI != null && m.CPI < 1 ? 'over budget' : 'on / under budget'} accent={m.CPI != null && m.CPI < 0.9 ? 'rose' : 'emerald'} testid="evm-kpi-cpi" />
            <KPI label="SPI · Schedule Performance" value={fmtIdx(m.SPI)} sub={m.SPI != null && m.SPI < 1 ? 'behind schedule' : 'on / ahead'} accent={m.SPI != null && m.SPI < 0.9 ? 'rose' : 'emerald'} testid="evm-kpi-spi" />
          </div>

          {/* Forecast */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <KPI label="EAC · Estimate at Completion" value={inrCompact(m.EAC)} sub="BAC / CPI" accent="indigo" testid="evm-kpi-eac" />
            <KPI label="ETC · Estimate to Complete" value={inrCompact(m.ETC)} sub="EAC − AC" accent="indigo" testid="evm-kpi-etc" />
            <KPI label="VAC · Variance at Completion" value={<VarianceCell value={m.VAC} />} sub="BAC − EAC" accent={m.VAC != null && m.VAC >= 0 ? 'emerald' : 'rose'} testid="evm-kpi-vac" />
          </div>

          {/* S-curve */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-5" data-testid="evm-s-curve">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Schedule Performance</div>
                <h3 className="font-display font-bold text-base mt-1">S-Curve · {showBaseline && sCurve?.has_baseline ? 'Baseline vs ' : ''}PV vs EV vs AC</h3>
              </div>
              <div className="flex items-center gap-3">
                {sCurve?.has_baseline ? (
                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 cursor-pointer select-none" data-testid="cc-baseline-toggle">
                    <input
                      type="checkbox"
                      checked={showBaseline}
                      onChange={e => setShowBaseline(e.target.checked)}
                      className="accent-[#7c3aed] w-3.5 h-3.5"
                    />
                    Show baseline plan
                  </label>
                ) : (
                  <span className="text-[10px] text-slate-400 italic" data-testid="cc-no-baseline">No frozen baseline — freeze it in the Gantt view</span>
                )}
                <Activity size={16} className="text-slate-400" />
              </div>
            </div>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 24, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => inrCompact(v)} width={70} />
                  <Tooltip formatter={(v) => inrCompact(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {showBaseline && sCurve?.has_baseline && (
                    <Line type="monotone" dataKey="baseline_PV" stroke="#7c3aed" strokeWidth={2} strokeDasharray="2 3" dot={false} name="Baseline Plan" />
                  )}
                  <Line type="monotone" dataKey="PV" stroke="#64748b" strokeWidth={2} strokeDasharray="6 4" dot={false} name="Current Plan (PV)" />
                  <Line type="monotone" dataKey="EV" stroke="#059669" strokeWidth={2} dot={false} name="Earned Value (EV)" connectNulls={false} />
                  <Line type="monotone" dataKey="AC" stroke="#dc2626" strokeWidth={2} dot={false} name="Actual Cost (AC)" connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AC Breakdown */}
          {evm?.ac_breakdown && (
            <div className="bg-white border border-slate-200 rounded-sm p-4 mb-5" data-testid="evm-ac-breakdown">
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">AC Breakdown</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Materials Received (GRN)</div>
                  <div className="font-display font-bold text-base mt-1">{inrCompact(evm.ac_breakdown.materials_received)}</div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Subcontractor Bills (RA)</div>
                  <div className="font-display font-bold text-base mt-1">{inrCompact(evm.ac_breakdown.subcontractor_bills)}</div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Total AC</div>
                  <div className="font-display font-bold text-base mt-1">{inrCompact(evm.ac_breakdown.total)}</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Portfolio */}
      {portfolio && portfolio.projects?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="evm-portfolio">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Portfolio · {portfolio.project_count} projects</div>
              <h3 className="font-display font-bold text-sm mt-0.5">
                BAC {inrCompact(portfolio.portfolio.BAC)} · EV {inrCompact(portfolio.portfolio.EV)} · AC {inrCompact(portfolio.portfolio.AC)} · CPI {fmtIdx(portfolio.portfolio.CPI)} · SPI {fmtIdx(portfolio.portfolio.SPI)}
              </h3>
            </div>
            <StatusBadge status={portfolio.portfolio.status} />
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-display font-bold uppercase tracking-widest text-[10px]">Project</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">BAC</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">EV</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">AC</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">CPI</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">SPI</th>
                <th className="text-right px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">EAC</th>
                <th className="text-center px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.projects.map(r => (
                <tr key={r.project_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setProjectId(r.project_id)} data-testid={`evm-row-${r.code || r.project_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-display font-semibold">{r.code || '—'}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[300px]">{r.name}</div>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">{inrCompact(r.metrics.BAC)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{inrCompact(r.metrics.EV)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{inrCompact(r.metrics.AC)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmtIdx(r.metrics.CPI)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{fmtIdx(r.metrics.SPI)}</td>
                  <td className="text-right px-3 py-2 tabular-nums">{inrCompact(r.metrics.EAC)}</td>
                  <td className="text-center px-3 py-2"><StatusBadge status={r.metrics.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
